// @flow
import { PDFFillerContext } from 'pages/Contexts/PdfFillerContext'
import * as React from 'react'
import Select from 'react-select'
import Button from 'ui/atoms/Button'
import Flex from 'ui/atoms/Flex'
import FlexBox from 'ui/atoms/FlexBox'
import { MagnifyingGlassMinus, MagnifyingGlassPlus } from 'ui/Icons'

function ZoomControl(): React.Node {
  const [fit, setFit] = React.useState(true)

  const { zoom, setZoom, pagesContainerRef, pages } =
    React.useContext(PDFFillerContext)

  const options = React.useMemo(
    () => [
      { label: '100%', value: 1 },
      { label: '75%', value: 0.75 },
      { label: '50%', value: 0.5 },
      { label: '25%', value: 0.25 },
      { label: 'Zoom to Fit', value: 'fit' },
    ],
    []
  )

  const selectedOption = React.useMemo(() => {
    if (fit) {
      return options[options.length - 1]
    }

    const zoomValue = Math.round(zoom * 100)

    return {
      label: `${zoomValue}%`,
      value: zoom,
    }
  }, [fit, options, zoom])

  const calculateZoomToFit = React.useCallback(() => {
    if (!pagesContainerRef.current) return

    const bounding = pagesContainerRef.current.getBoundingClientRect()
    const firstPage = pages?.[0]
    const pageWidth = firstPage?.width

    if (bounding?.width && pageWidth) {
      const newZoom = bounding?.width / pageWidth
      setZoom(newZoom)
      setFit(true)
    }
  }, [pages, pagesContainerRef, setZoom])

  const changeZoom = React.useCallback(
    (option) => {
      if (option.value === 'fit') {
        return calculateZoomToFit()
      }

      setZoom(option.value)
      setFit(false)
    },
    [calculateZoomToFit, setZoom]
  )

  const increaseZoom = React.useCallback(() => {
    const newZoom = Math.min(zoom + 0.1, 1)
    setZoom(newZoom)
    setFit(false)
  }, [zoom, setZoom])

  const decreaseZoom = React.useCallback(() => {
    const newZoom = Math.max(zoom - 0.1, 0.1)
    setZoom(newZoom)
    setFit(false)
  }, [zoom, setZoom])

  React.useEffect(() => {
    if (fit) {
      calculateZoomToFit()
    }

    window.addEventListener('resize', () => {
      if (fit) {
        calculateZoomToFit()
      }
    })

    return () => {
      window.removeEventListener('resize', () => () => {
        if (fit) {
          calculateZoomToFit()
        }
      })
    }
  }, [fit, calculateZoomToFit])

  return (
    <Flex $horizontalSpacing="small">
      <FlexBox>
        <Select
          onChange={changeZoom}
          options={options}
          value={selectedOption}
        />
      </FlexBox>
      <Button $compact $noBorder $noOutline onClick={decreaseZoom}>
        <MagnifyingGlassMinus />
      </Button>
      <Button $compact $noBorder $noOutline onClick={increaseZoom}>
        <MagnifyingGlassPlus />
      </Button>
    </Flex>
  )
}

export default ZoomControl
